
import Vue from 'vue';
import { Loading } from 'vant';
import { PageFlip } from 'page-flip';
import { getReaderConfig } from '../api';
import {
  createMergedPages,
  getNeighborPageNumbers,
  getPageViewportSize,
  loadPdfSectionDocuments,
  PdfMergedPage,
  PdfSectionDocument,
  renderPageImage,
} from '../utils/pdf';

const INITIAL_PRIORITY_RADIUS = 0;
const ACTIVE_PRIORITY_RADIUS = 1;
const PREFETCH_RADIUS = 4;
const CACHE_RADIUS = 6;
const STAGE_SWIPE_DISTANCE = 18;
const STAGE_SWIPE_MAX_DURATION = 600;
const BACKGROUND_RENDER_IDLE_DELAY = 700;

Vue.use(Loading);

export default Vue.extend({
  data() {
    return {
      title: '',
      loading: true,
      errorText: '',
      sectionDocuments: [] as PdfSectionDocument[],
      mergedPages: [] as PdfMergedPage[],
      totalPages: 0,
      currentPage: 1,
      bookWidth: 320,
      bookHeight: 480,
      pageImageMap: {} as { [key: number]: string },
      renderedPageMap: {} as { [key: number]: boolean },
      pageLoadingMap: {} as { [key: number]: boolean },
      pageFailedMap: {} as { [key: number]: boolean },
      renderingSet: {} as { [key: number]: boolean },
      backgroundRenderToken: 0,
      resizeTimer: null as number | null,
      revealTimer: null as number | null,
      backgroundRenderTimer: null as number | null,
      flipBookVisible: false,
      pageFlip: null as PageFlip | null,
      pageFlipHandler: null as ((event: any) => void) | null,
      leavingReader: false,
      userInteracting: false,
      stageTouchStartX: 0,
      stageTouchStartY: 0,
      stageTouchStartTime: 0,
      stageTouchMoving: false,
    };
  },
  computed: {
    activePageLoading(): boolean {
      return !!this.pageLoadingMap[this.currentPage];
    },
    activePageFailed(): boolean {
      return !!this.pageFailedMap[this.currentPage];
    },
    shouldShowStageLoading(): boolean {
      return !this.flipBookVisible || this.activePageLoading;
    },
    pageNumbers(): number[] {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    },
    flipBookStyle(): { width: string; height: string } {
      return {
        width: `${this.bookWidth}px`,
        height: `${this.bookHeight}px`,
      };
    },
  },
  async mounted() {
    await this.initialize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.backgroundRenderToken += 1;

    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer);
    }

    if (this.revealTimer) {
      window.clearTimeout(this.revealTimer);
    }

    if (this.backgroundRenderTimer) {
      window.clearTimeout(this.backgroundRenderTimer);
    }

    this.destroyPageFlip();
  },
  methods: {
    async initialize() {
      try {
        const response = await getReaderConfig();
        const config = response.data;
        const sectionDocuments = await loadPdfSectionDocuments(config);
        const mergedPages = createMergedPages(sectionDocuments);
        const totalPages = mergedPages.length;
        const targetPage = this.getTargetPage(totalPages);
        const firstDocument = sectionDocuments[0];

        if (!firstDocument) {
          throw new Error('No pdf document available');
        }

        const pageViewport = await getPageViewportSize(
          firstDocument.document,
          1,
        );

        this.title = config.title;
        document.title = config.title;
        this.sectionDocuments = sectionDocuments;
        this.mergedPages = mergedPages;
        this.totalPages = totalPages;
        this.currentPage = targetPage;
        this.leavingReader = false;
        this.updateBookSize(pageViewport.width, pageViewport.height);
        this.flipBookVisible = false;
        this.loading = false;

        await this.$nextTick();
        await this.renderPriorityPages(targetPage, INITIAL_PRIORITY_RADIUS);
        await this.$nextTick();
        this.createPageFlip(targetPage);
        this.revealFlipBook(0);
        this.scheduleBackgroundRenderAfterIdle(targetPage);
      } catch (error) {
        this.loading = false;
        this.errorText = 'PDF 加载失败，请稍后再试';
      }
    },
    getTargetPage(totalPages: number) {
      const pageValue = Number(this.$route.query.page || 1);

      if (Number.isNaN(pageValue)) {
        return 1;
      }

      return Math.min(Math.max(pageValue, 1), totalPages);
    },
    updateBookSize(pageWidth: number, pageHeight: number) {
      const safeViewportWidth = Math.max(window.innerWidth - 32, 280);
      const safeViewportHeight = Math.max(window.innerHeight - 96, 360);
      const ratio = pageWidth / pageHeight;
      let nextWidth = Math.min(safeViewportWidth, 420);
      let nextHeight = nextWidth / ratio;

      if (nextHeight > safeViewportHeight) {
        nextHeight = safeViewportHeight;
        nextWidth = nextHeight * ratio;
      }

      this.bookWidth = Math.floor(nextWidth);
      this.bookHeight = Math.floor(nextHeight);
    },
    getFlipPages() {
      const refs = this.$refs.flipPages as
        | HTMLElement[]
        | HTMLElement
        | undefined;

      if (!refs) {
        return [] as HTMLElement[];
      }

      return Array.isArray(refs) ? refs : [refs];
    },
    getPageAlt(pageNumber: number) {
      const mergedPage = this.mergedPages[pageNumber - 1];

      if (!mergedPage) {
        return `第 ${pageNumber} 页`;
      }

      return mergedPage.displayTitle;
    },
    getPageImage(pageNumber: number) {
      return this.pageImageMap[pageNumber] || '';
    },
    isPageFailed(pageNumber: number) {
      return !!this.pageFailedMap[pageNumber];
    },
    destroyPageFlip() {
      if (!this.pageFlip) {
        return;
      }

      this.pageFlip.destroy();
      this.pageFlip = null;
      this.pageFlipHandler = null;
    },
    revealFlipBook(delay = 0) {
      this.flipBookVisible = false;

      if (this.revealTimer) {
        window.clearTimeout(this.revealTimer);
      }

      if (delay <= 0) {
        this.flipBookVisible = true;
        return;
      }

      this.revealTimer = window.setTimeout(() => {
        this.flipBookVisible = true;
      }, delay);
    },
    createPageFlip(startPage: number) {
      const container = this.$refs.flipBook as HTMLElement | undefined;
      const pageElements = this.getFlipPages();

      this.destroyPageFlip();

      if (!container || !pageElements.length) {
        return;
      }

      const pageFlip = new PageFlip(container, {
        width: this.bookWidth,
        height: this.bookHeight,
        size: 'fixed',
        usePortrait: true,
        showCover: false,
        drawShadow: true,
        autoSize: true,
      });

      this.pageFlipHandler = (event: any) => {
        this.handleFlip(event);
      };

      pageFlip.on('flip', this.pageFlipHandler);
      pageFlip.loadFromHTML(pageElements);
      this.pageFlip = pageFlip;
      this.syncReaderPage(startPage);
    },
    async renderPriorityPages(
      pageNumber: number,
      radius = ACTIVE_PRIORITY_RADIUS,
      force = false,
    ) {
      this.cancelBackgroundRender();
      const targets = getNeighborPageNumbers(
        pageNumber,
        this.totalPages,
        radius,
      );

      for (let index = 0; index < targets.length; index += 1) {
        await this.renderPage(targets[index], force);
      }
    },
    cancelBackgroundRender() {
      this.backgroundRenderToken += 1;

      if (this.backgroundRenderTimer) {
        window.clearTimeout(this.backgroundRenderTimer);
        this.backgroundRenderTimer = null;
      }
    },
    markUserInteracting(value: boolean) {
      this.userInteracting = value;

      if (value) {
        this.cancelBackgroundRender();
      }
    },
    scheduleBackgroundRenderAfterIdle(pageNumber: number) {
      this.cancelBackgroundRender();

      if (this.userInteracting) {
        return;
      }

      this.backgroundRenderTimer = window.setTimeout(() => {
        this.backgroundRenderTimer = null;

        if (this.userInteracting) {
          return;
        }

        this.scheduleBackgroundRender(pageNumber);
      }, BACKGROUND_RENDER_IDLE_DELAY);
    },
    scheduleBackgroundRender(pageNumber: number) {
      if (this.userInteracting) {
        return;
      }

      const token = this.backgroundRenderToken + 1;
      const queue = getNeighborPageNumbers(
        pageNumber,
        this.totalPages,
        PREFETCH_RADIUS,
      ).filter((item) => Math.abs(item - pageNumber) > ACTIVE_PRIORITY_RADIUS);

      this.backgroundRenderToken = token;

      void (async () => {
        for (let index = 0; index < queue.length; index += 1) {
          if (token !== this.backgroundRenderToken || this.userInteracting) {
            return;
          }

          await this.renderPage(queue[index]);

          if (token !== this.backgroundRenderToken || this.userInteracting) {
            return;
          }

          await this.waitForNextFrame();
          await this.waitForNextFrame();
        }
      })();
    },
    cleanupFarImages(activePage: number) {
      const keepPages = getNeighborPageNumbers(
        activePage,
        this.totalPages,
        CACHE_RADIUS,
      );
      const keepPageMap = keepPages.reduce(
        (result, pageNumber) => ({
          ...result,
          [pageNumber]: true,
        }),
        {} as { [key: number]: boolean },
      );

      Object.keys(this.pageImageMap).forEach((key) => {
        const pageNumber = Number(key);

        if (keepPageMap[pageNumber] || this.pageLoadingMap[pageNumber]) {
          return;
        }

        this.$delete(this.pageImageMap, pageNumber);
        this.$set(this.renderedPageMap, pageNumber, false);
      });
    },
    waitForNextFrame() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => resolve(undefined));
      });
    },
    async renderPage(pageNumber: number, force = false) {
      if (!this.sectionDocuments.length || !this.mergedPages.length) {
        return;
      }

      if (this.renderingSet[pageNumber]) {
        return;
      }

      if (this.renderedPageMap[pageNumber] && !force) {
        return;
      }

      this.$set(this.renderingSet, pageNumber, true);
      this.$set(this.pageLoadingMap, pageNumber, true);
      this.$set(this.pageFailedMap, pageNumber, false);

      try {
        const mergedPage = this.mergedPages[pageNumber - 1];

        if (!mergedPage) {
          throw new Error('Merged page not found');
        }

        const sectionDocument = this.sectionDocuments[mergedPage.documentIndex];

        if (!sectionDocument) {
          throw new Error('Section document not found');
        }

        const imageUrl = await renderPageImage(
          sectionDocument.document,
          mergedPage.sectionPageNumber,
          this.bookWidth,
        );

        this.$set(this.pageImageMap, pageNumber, imageUrl);
        this.$set(this.renderedPageMap, pageNumber, true);
      } catch (error) {
        this.$set(this.pageFailedMap, pageNumber, true);
      }

      this.$set(this.pageLoadingMap, pageNumber, false);
      this.$delete(this.renderingSet, pageNumber);
    },
    syncReaderPage(pageNumber: number) {
      if (!this.pageFlip) {
        return;
      }

      this.pageFlip.turnToPage(pageNumber - 1);
    },
    handleStageTouchStart(event: TouchEvent) {
      if (!event.changedTouches.length || !this.flipBookVisible) {
        return;
      }

      this.markUserInteracting(true);

      const touch = event.changedTouches[0];

      this.stageTouchStartX = touch.clientX;
      this.stageTouchStartY = touch.clientY;
      this.stageTouchStartTime = Date.now();
      this.stageTouchMoving = true;
    },
    handleStageTouchMove() {
      if (!this.stageTouchMoving) {
        return;
      }
    },
    handleStageTouchEnd(event: TouchEvent) {
      if (!this.stageTouchMoving || !event.changedTouches.length) {
        this.resetStageTouch();
        return;
      }

      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.stageTouchStartX;
      const deltaY = touch.clientY - this.stageTouchStartY;
      const duration = Date.now() - this.stageTouchStartTime;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      this.resetStageTouch();
      this.markUserInteracting(false);
      this.scheduleBackgroundRenderAfterIdle(this.currentPage);

      if (
        !this.pageFlip ||
        !this.flipBookVisible ||
        this.shouldShowStageLoading
      ) {
        return;
      }

      if (duration > STAGE_SWIPE_MAX_DURATION) {
        return;
      }

      if (absDeltaX < STAGE_SWIPE_DISTANCE) {
        return;
      }

      if (absDeltaX <= absDeltaY * 0.6) {
        return;
      }

      if (deltaX > 0) {
        this.pageFlip.flipPrev('top');
        return;
      }

      this.pageFlip.flipNext('top');
    },
    handleStageTouchCancel() {
      this.resetStageTouch();
      this.markUserInteracting(false);
      this.scheduleBackgroundRenderAfterIdle(this.currentPage);
    },
    resetStageTouch() {
      this.stageTouchStartX = 0;
      this.stageTouchStartY = 0;
      this.stageTouchStartTime = 0;
      this.stageTouchMoving = false;
    },
    handleFlip(event: any) {
      if (this.leavingReader) {
        return;
      }

      const pageIndex = Number(event && event.data);

      if (Number.isNaN(pageIndex)) {
        return;
      }

      const nextPage = Math.min(Math.max(pageIndex + 1, 1), this.totalPages);

      this.currentPage = nextPage;
      this.cleanupFarImages(nextPage);
      this.$router
        .replace({
          name: '20260525SQFLZCZN-reader',
          query: {
            page: String(nextPage),
            index: String(pageIndex),
          },
        })
        .catch(() => undefined);
      void this.renderPriorityPages(nextPage);
      this.scheduleBackgroundRenderAfterIdle(nextPage);
    },
    handleResize() {
      if (!this.sectionDocuments.length) {
        return;
      }

      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = window.setTimeout(async () => {
        const pageViewport = await getPageViewportSize(
          this.sectionDocuments[0].document,
          1,
        );

        this.updateBookSize(pageViewport.width, pageViewport.height);
        this.flipBookVisible = false;
        await this.renderPriorityPages(
          this.currentPage,
          ACTIVE_PRIORITY_RADIUS,
          true,
        );
        await this.$nextTick();
        this.createPageFlip(this.currentPage);
        this.revealFlipBook(0);
        this.scheduleBackgroundRenderAfterIdle(this.currentPage);
      }, 160);
    },
    handleBack() {
      this.leavingReader = true;
      this.$router.push({
        name: '20260525SQFLZCZN-home',
        query: {
          page: String(this.currentPage),
          index: String(this.currentPage - 1),
        },
      });
    },
  },
});
